/* Ability Warehouse — shared "describe it in your own words" parser.
   Turns a typed sentence ("Mum can't get up off the toilet, under $200") into choices on three axes
   (where / what's hard / who), a price range and leftover search words. Used by assets/product-match.js
   (the Product Match finder) and assets/aw-choose.js (the collection chooser's default question set).
   Works on records from the shared product index (window.CWIndex): title t, tags tg, spec use_for uf. */
(function () {
  'use strict';
  if (window.AWUnderstand) return;

  /* ---------- helpers over an index item ---------- */
  function tag(it, t) { return (it.tg || []).indexOf(t) > -1; }
  function uf(it, word) { return String(it.uf || '').toLowerCase().indexOf(word) > -1; }
  function text(it) { return (it.t + ' ' + (it.tg || []).join(' ').replace(/-/g, ' ') + ' ' + (it.uf || '')).toLowerCase(); }
  function re(r) { return function (it) { return r.test(text(it)); }; }
  function any() { var fns = Array.prototype.slice.call(arguments); return function (it) { for (var i = 0; i < fns.length; i++) if (fns[i](it)) return true; return false; }; }

  /* ---------- predicates per axis/option (shared with Product Match's steps) ---------- */
  var tests = {
    where: {
      bathroom: any(function (it) { return tag(it, 'need-bathroom') || uf(it, 'bathroom') || uf(it, 'shower') || uf(it, 'bath'); }, re(/\b(shower|bath|bathroom)\b/)),
      toilet: any(function (it) { return tag(it, 'need-toilet') || uf(it, 'toilet'); }, re(/\b(toilet|commode|toileting)\b/)),
      bedroom: any(function (it) { return tag(it, 'need-bedroom') || uf(it, 'bed'); }, re(/\b(bed|bedroom|mattress|overbed|pillow)\b/)),
      walking: any(function (it) { return tag(it, 'need-walking') || tag(it, 'need-wheelchairs') || tag(it, 'dept-mobility'); }, re(/\b(walk|rollator|wheelchair|scooter|cane|crutch|ramp)/)),
      home: any(function (it) { return tag(it, 'need-daily-living') || tag(it, 'need-smart-home') || tag(it, 'dept-living-aids') || tag(it, 'dept-smart-home'); }, re(/\b(kitchen|dressing|reacher|household|smart|sensor|doorbell|garden)\b/))
    },
    what: {
      standing: any(function (it) { return tag(it, 'need-toilet') || uf(it, 'toilet'); }, re(/\b(frame|raised|riser|lift chair|recliner|rail|stand|grab)\b/)),
      showering: any(function (it) { return uf(it, 'shower'); }, re(/\b(shower|bath board|bath seat|transfer bench|long handled sponge)\b/)),
      walking: any(function (it) { return tag(it, 'need-walking'); }, re(/\b(walking|walker|rollator|cane|stick|crutch|frame)\b/)),
      transferring: re(/\b(transfer|slide|sling|hoist|turntable|belt|lift|board)\b/),
      reaching: any(function (it) { return tag(it, 'need-daily-living'); }, re(/\b(reacher|reaching|long handled|sock|dressing|shoe horn|pick ?up)\b/)),
      continence: any(function (it) { return tag(it, 'need-incontinence') || tag(it, 'dept-incontinence'); }, re(/\b(incontinence|pads?|pants|nappies|briefs|protector|urinal|catheter)\b/)),
      pain: any(function (it) { return tag(it, 'need-braces') || tag(it, 'dept-braces-support'); }, re(/\b(brace|support|splint|tens|heat|compression|cushion)\b/)),
      safety: any(function (it) { return tag(it, 'need-smart-home') || tag(it, 'dept-smart-home'); }, re(/\b(alarm|alert|sensor|fall|pendant|doorbell|monitor|camera|smart)\b/))
    },
    who: {
      me: function () { return true; },
      carer: function () { return true; },
      client: function () { return true; }
    }
  };
  var single = { where: true, what: false, who: true };
  var labels = {
    where: { bathroom: 'Bathroom', toilet: 'Toilet', bedroom: 'Bedroom', walking: 'Getting around', home: 'Around the home' },
    what: { standing: 'Standing up', showering: 'Showering', walking: 'Walking', transferring: 'Transferring', reaching: 'Reaching', continence: 'Continence', pain: 'Pain / support', safety: 'Safety alerts' },
    who: { me: 'Myself', carer: 'Someone I care for', client: 'A client / participant' }
  };

  /* words in a typed request → choices */
  var PHRASES = [
    ['where', 'toilet', /\b(toilet|loo|commode|toileting)\b/],
    ['where', 'bathroom', /\b(shower|bath|bathroom|ensuite|wet area)\b/],
    ['where', 'bedroom', /\b(bed|bedroom|night|mattress)\b/],
    ['where', 'walking', /\b(walk|walker|walking|rollator|wheelchair|scooter|shops|outside|outdoors|frame|cane|stick)\b/],
    ['where', 'home', /\b(kitchen|cooking|dressing|garden|house|home|reach|door|alarm|sensor)\b/],
    ['what', 'standing', /\b(get up|getting up|stand|standing|rise|rising|off the|out of the chair|lift)\b/],
    ['what', 'showering', /\b(shower|showering|bath|bathing|wash)\b/],
    ['what', 'walking', /\b(walk|walking|unsteady|wobbly|balance|steps|far|distance|shops)\b/],
    ['what', 'transferring', /\b(transfer|transferring|move from|into bed|out of bed|into the car|slide)\b/],
    ['what', 'reaching', /\b(reach|reaching|bend|bending|floor|socks|shoes|dress|dressing|pick up)\b/],
    ['what', 'continence', /\b(continence|incontinence|leak|leaks|pads|nappies|wet the bed|bladder)\b/],
    ['what', 'pain', /\b(pain|sore|ache|brace|support|knee|back|wrist|ankle|arthritis|swelling)\b/],
    ['what', 'safety', /\b(fall|falls|fell|alarm|alert|safe|safety|wander|sensor|doorbell|dementia)\b/],
    ['who', 'carer', /\b(mum|dad|mother|father|nan|nanna|pop|grandma|grandpa|husband|wife|partner|son|daughter|my (friend|brother|sister))\b/],
    ['who', 'client', /\b(client|participant|resident|patient)\b/],
    ['who', 'me', /\b(i can'?t|i cannot|myself|for me|my own)\b/]
  ];
  var CHATTER = /\b(mum|dad|mother|father|nan|nanna|pop|grandma|grandpa|husband|wife|partner|son|daughter|my|i|me|can'?t|cannot|can|get|up|off|the|a|an|and|in|on|for|to|of|is|are|with|has|have|needs?|wants?|help|helps|please|something|anything|unit|rental|rent|renting|landlord|home|house|at|from|into|out|very|really|bit|some|shops|floor|toilet|shower|bath|bathroom|bed|bedroom|unsteady|reach)\b/g;

  /* parse(raw) → { sel:{where:[],what:[],who:[]}, min, max, q, picked:[labels], rental }
     `q` is the leftover product words for CWIndex.search (chatter and mapped nouns stripped). */
  function parse(raw) {
    var t = ' ' + String(raw || '').toLowerCase().replace(/×/g, 'x') + ' ';
    var out = { sel: { where: [], what: [], who: [] }, min: 0, max: Infinity, q: '', picked: [], rental: false };
    var under = t.match(/(?:under|below|less than|max|up to)\s*\$?\s*(\d+)/);
    var over = t.match(/(?:over|above|more than|from|min)\s*\$?\s*(\d+)/);
    if (under) { out.max = +under[1]; t = t.replace(under[0], ' '); out.picked.push('under $' + under[1]); }
    if (over) { out.min = +over[1]; t = t.replace(over[0], ' '); out.picked.push('over $' + over[1]); }
    out.rental = /\b(rent|rental|renting|landlord|strata|unit)\b/.test(t);
    PHRASES.forEach(function (p) {
      var axis = p[0], v = p[1];
      if (!t.match(p[2])) return;
      if (single[axis] && out.sel[axis].length) return;
      if (out.sel[axis].indexOf(v) < 0) out.sel[axis].push(v);
      var l = labels[axis][v];
      if (l && out.picked.indexOf(l) < 0) out.picked.push(l);
    });
    out.q = t.replace(CHATTER, ' ').replace(/[^\w\s-]/g, ' ').replace(/\s+/g, ' ').trim();
    return out;
  }

  /* does one index record pass the parsed choices (AND across axes, OR within; budget on the paid price)? */
  function passes(it, parsed, opts) {
    opts = opts || {};
    var axes = ['where', 'what', 'who'];
    for (var a = 0; a < axes.length; a++) {
      var sel = parsed.sel[axes[a]] || [];
      if (!sel.length) continue;
      var ok = false;
      for (var i = 0; i < sel.length; i++) { var fn = tests[axes[a]][sel[i]]; if (fn && fn(it)) { ok = true; break; } }
      if (!ok) return false;
    }
    if (opts.noBudget) return true;
    var d = (it.per || it.p || 0) / 100;
    return d >= parsed.min && d <= parsed.max;
  }

  window.AWUnderstand = { parse: parse, passes: passes, tests: tests, labels: labels, single: single, PHRASES: PHRASES, helpers: { tag: tag, uf: uf, text: text, re: re, any: any } };
})();
